<template>
  <div class="gs-app gs-refresh-controls gs-share-embed-panel">
    <gs-card class="gs-share-embed-panel__publish">
      <publish-video
        :video-id="embedVideo.video_id"
        :is-published="embedVideo.is_published"
        :publishes-left="publishesLeft"
        @isLoading="$emit('setIsLoading', $event)"
        @updatePublishLimit="onUpdatePublishLimit($event)"
      />
    </gs-card>

    <template v-if="embedVideo.is_published">
      <gs-card
        v-if="
          !status || (model.video_source_type === 2 && model.is_uploading) || model.temp_video_source
        "
        class="gs-share-embed-panel__notice"
      >
        {{
          model.video_source_type === 2
            ? $t('frontend_groovevideo_step4_video_uploading_to_yt_info')
            : $t('frontend_groovevideo_step4_video_encoding_info')
        }}
      </gs-card>

      <template v-else-if="showLinks">
        <gs-card class="gs-share-embed-panel__row">
          <copy-row
            label="Public URL"
            :value="embedVideo.hosted_page_link"
            :primary="true"
            :openable="true"
            @open="redirectToPage(embedVideo.hosted_page_link)"
            @copied="onCopyVideoPageUrl"
            @copy-failed="onErrorVideoPageUrl"
          />

          <div class="gs-share-embed-panel__toggles">
            <label class="gs-switch-row gs-share-embed-panel__toggle">
              <span class="gs-switch-row__text">
                <span class="gs-switch-row__label">{{ $t('frontend_video_autoplay') }}</span>
              </span>
              <gd-toggle
                v-model="embedVideo.hosted_page_settings.autoplay"
                @change="updateHostedPageSettings"
              />
            </label>
            <label
              v-if="embedVideo.video_source_type == 1"
              class="gs-switch-row gs-share-embed-panel__toggle"
              v-tooltip.right="{
                content: !embedVideo.download_available
                  ? $t('frontend_video_download_unavailable')
                  : '',
                classes: 'groovevideo-videos-tooltip',
                delay: {
                  show: 200,
                  hide: 200,
                },
              }"
            >
              <span class="gs-switch-row__text">
                <span class="gs-switch-row__label">{{ $t('frontend_video_allow_download') }}</span>
              </span>
              <gd-toggle
                v-model="embedVideo.hosted_page_settings.allow_download"
                @change="checkDownloadable"
              />
            </label>
          </div>
          <p class="gs-share-embed-panel__note">
            *{{
              $t('frontend_groovevideo_autoplay_will_start_muted_notification') +
              $t('frontend_groovevideo_autoplay_loop_in_background')
            }}
          </p>
        </gs-card>

        <gs-card class="gs-share-embed-panel__row">
          <copy-row
            :label="$t('frontend_video_embed_code')"
            :value="embedVideo.video_embed"
            :multiline="true"
            @copied="onCopyEmbeddedCode"
            @copy-failed="onErrorEmbeddedCode"
          />
        </gs-card>

        <gs-card class="gs-share-embed-panel__row">
          <copy-row
            v-if="!model.is_uploading"
            :label="$t('frontend_video_source_file_url')"
            :value="sourceFileUrl"
            :openable="true"
            @open="redirectToPage(sourceFileUrl)"
            @copied="onCopySourceFileUrl"
            @copy-failed="onErrorSourceFileUrl"
          />
          <p v-else class="gs-share-embed-panel__note">
            {{ $t('frontend_groovevideo_links_source_url_unavailable') }}
          </p>
        </gs-card>
      </template>

      <gs-card v-else class="gs-share-embed-panel__notice">
        {{ $t('frontend_groovevideo_video_index_upload_embed_code') }}
      </gs-card>
    </template>

    <p v-else class="gs-share-embed-panel__hint">
      <gs-icon name="link" size="14" />
      <span>{{ $t('frontend_groovevideo_links_publish_video_to_activate') }}</span>
    </p>
  </div>
</template>

<script>
import Notify from '@groovepages/gd-lib/notify';
import EmbedPageSettings from '@/models/EmbedPageSettings';
import PublishVideo from '@/components/PublishVideo';
import Video from '@/models/Video';
import CopyRow from '@/components/library/CopyRow.vue';

// `model` reaches this panel either as the `Video` model instance `Video.$find`
// returned, or as a plain object once the shell's default-injection spread
// (`{ ...this.model, settings: {...} }`) has stripped the prototype. Either
// way the two derived links need the exact formula the `Video` class getters
// use, so they are always read through the class's own getter descriptor
// rather than duplicated here — this works whether or not `model` still has
// `Video.prototype` in its chain.
function readVideoGetter(model, name) {
  return Object.getOwnPropertyDescriptor(Video.prototype, name).get.call(model);
}

export default {
  name: 'ShareEmbedPanel',

  // The shell hands every panel the same props; the ones this panel does not
  // declare would otherwise land on its root element.
  inheritAttrs: false,

  components: {
    PublishVideo,
    CopyRow,
  },

  props: {
    model: {
      type: Object,
      required: true,
    },
    publishesLeft: {
      type: Number,
      default: 0,
    },
    status: { default: true },
  },

  data() {
    return {
      showLinks: false,
      // `v-model` writes the new value before `@change` fires, so the values
      // the server last accepted have to be kept separately for a revert.
      lastSavedHostedPageSettings: { autoplay: 0, allow_download: 0 },
      embedVideo: {
        is_published: 0,
        video_embed: '',
        hosted_page_link: '',
        hosted_page_settings: {
          autoplay: 0,
          allow_download: 0,
        },
        video_source: '',
        video_id: '',
        video_source_type: '',
        download_available: 0,
      },
    };
  },

  computed: {
    sourceFileUrl() {
      return this.model.is_local &&
        this.model.video_stream_source &&
        this.model.is_downloadable !== 1
        ? this.model.video_stream_source
        : this.model.video_source;
    },
  },

  watch: {
    status() {
      this.getVideoLinks(this.model);
    },

    // Replacing the source rewrites these two keys without touching `status`,
    // so the links have to be recomputed off them as well — otherwise a
    // legacy video switched to a YouTube link keeps showing the "upload to get
    // an embed code" notice until the user leaves and re-enters the editor.
    'model.video_source': function onVideoSourceChange() {
      this.getVideoLinks(this.model);
    },

    'model.video_source_type': function onVideoSourceTypeChange() {
      this.getVideoLinks(this.model);
    },
  },

  created() {
    this.getVideoLinks(this.model);
  },

  // The shell keeps every panel alive, so `created()` fires once. Re-read the
  // links each time the tab is shown again.
  activated() {
    this.getVideoLinks(this.model);
  },

  methods: {
    getVideoLinks(video) {
      this.embedVideo.is_published = video.is_published;
      this.embedVideo.video_id = video.id;

      const videoEmbed = readVideoGetter(video, 'video_embed');
      const hostedPageLink = readVideoGetter(video, 'hostedPageLink');

      if (
        (typeof videoEmbed !== 'undefined' &&
          videoEmbed &&
          videoEmbed !== '' &&
          typeof video.video_source !== 'undefined' &&
          video.video_source &&
          video.video_source !== '' &&
          hostedPageLink) ||
        (video.is_local === 1 &&
          typeof video.video_stream_source !== 'undefined' &&
          video.video_stream_source !== null)
      ) {
        this.embedVideo.video_embed = videoEmbed;
        this.embedVideo.hosted_page_link = hostedPageLink;
        this.embedVideo.video_source = video.public_video_source;
        this.embedVideo.hosted_page_settings = video.hosted_page_settings;
        this.lastSavedHostedPageSettings = {
          autoplay: video.hosted_page_settings ? video.hosted_page_settings.autoplay : 0,
          allow_download: video.hosted_page_settings
            ? video.hosted_page_settings.allow_download
            : 0,
        };
        this.embedVideo.video_source_type = video.video_source_type;
        this.embedVideo.download_available =
          video.video_source_type === 1 &&
          (!video.is_local || video.is_downloadable);
        this.showLinks = true;
      } else {
        this.showLinks = false;
      }
    },

    onUpdatePublishLimit(data) {
      this.embedVideo.is_published = data.isPublished;
      this.$emit('updatePublishLimit', data);
    },

    validate() {
      this.$emit('on-validate', true);
      return true;
    },

    redirectToPage(url) {
      if (url !== '') {
        window.open(url, '_blank');
      }
    },

    onCopyEmbeddedCode() {
      Notify.success(this.$t('frontend_groovevideo_videos_wait_video_embed'));
    },

    onErrorEmbeddedCode() {
      Notify.fail(
        this.$t('frontend_groovevideo_videos_wait_video_unable_to_copy')
      );
    },

    onCopyVideoPageUrl() {
      Notify.success(
        this.$t('frontend_groovevideo_videos_wait_copy_video_page_url')
      );
    },

    onErrorVideoPageUrl() {
      Notify.fail(
        this.$t(
          'frontend_groovevideo_videos_wait_unable_to_copy_video_page_url'
        )
      );
    },

    onCopySourceFileUrl() {
      Notify.success(
        this.$t('frontend_groovevideo_videos_wait_copy_source_url')
      );
    },

    onErrorSourceFileUrl() {
      Notify.fail(
        this.$t(
          'frontend_groovevideo_videos_wait_video_unable_to_copy_source_url'
        )
      );
    },

    async checkDownloadable() {
      if (this.embedVideo.download_available === 0) {
        this.embedVideo.hosted_page_settings.allow_download = 0;
      } else {
        await this.updateHostedPageSettings();
      }
    },

    // `setIsLoading` drives the shell's full-page blocking spinner, and this
    // page holds unsaved Player and Marketing edits. A rejected request must
    // therefore always release the spinner and put the toggle back where the
    // server still has it, rather than freezing the editor behind it.
    async updateHostedPageSettings() {
      this.$emit('setIsLoading', true);
      const settings = this.embedVideo.hosted_page_settings;
      const previous = this.lastSavedHostedPageSettings;
      const attempted = {
        autoplay: settings.autoplay ? 1 : 0,
        allow_download: settings.allow_download ? 1 : 0,
      };
      const hostedSettings = new EmbedPageSettings(attempted);

      try {
        const savedSettings = await hostedSettings
          .for(this.model)
          .save();

        this.model.hosted_page_settings = savedSettings.data;
        this.lastSavedHostedPageSettings = attempted;

        this.embedVideo.hosted_page_link = readVideoGetter(this.model, 'hostedPageLink');
      } catch (error) {
        this.$set(settings, 'autoplay', previous.autoplay);
        this.$set(settings, 'allow_download', previous.allow_download);
        Notify.fail('We could not save the hosted page settings. Please try again.');
      } finally {
        this.$emit('setIsLoading', false);
      }
    },
  },
};
</script>

<style scoped>
.gs-share-embed-panel {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 18px;
}

.gs-share-embed-panel__publish {
  padding: 18px 20px;
}

.gs-share-embed-panel__row {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 18px 20px;
}

.gs-switch-row {
  display: flex;
  gap: 16px;
  align-items: flex-start;
  justify-content: space-between;
  padding: 14px 16px;
  background: var(--gs-surface-subtle);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
}

.gs-switch-row__text {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.gs-switch-row__label {
  color: var(--gs-foreground);
  font-size: 14px;
  font-weight: 600;
  line-height: 1.35;
}

.gs-switch-row__help {
  max-width: 56ch;
  color: var(--gs-muted-foreground);
  font-size: 13px;
  line-height: 1.5;
}

.gs-share-embed-panel__toggles {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.gs-share-embed-panel__toggle {
  flex: 1 1 220px;
  cursor: default;
}

.gs-share-embed-panel__note {
  margin: 0;
  color: var(--gs-muted-foreground);
  font-size: 12px;
  font-weight: 600;
}

.gs-share-embed-panel__notice {
  padding: 18px 20px;
  color: var(--gs-foreground);
  font-size: 15px;
  font-weight: 600;
}

.gs-share-embed-panel__hint {
  display: flex;
  gap: 8px;
  align-items: center;
  margin: 0;
  padding: 14px 16px;
  color: var(--gs-muted-foreground);
  background: var(--gs-surface-subtle);
  border-radius: var(--gs-radius-md);
  font-size: 13px;
}

@media (max-width: 640px) {
  .gs-share-embed-panel {
    padding: 14px;
  }
}
</style>
